import { useState, useEffect, useCallback } from 'react';
import {
  ShieldAlert, Loader2, AlertTriangle, Sparkles, Search, CheckCircle2, XCircle,
  HelpCircle, Trash2, ExternalLink, Info,
} from 'lucide-react';
import {
  getTokenScannerChains, scanToken, listRecentTokenScans, deleteTokenScan,
} from '../services/api';
import { InfoTip } from '../components/ui';

const DEFAULT_CHAIN = 'ethereum';

function statusIcon(status, size = 4) {
  const cls = `w-${size} h-${size} flex-shrink-0`;
  if (status === 'pass') return <CheckCircle2 className={`${cls} text-emerald-400`} />;
  if (status === 'fail') return <XCircle className={`${cls} text-red-400`} />;
  if (status === 'warn') return <AlertTriangle className={`${cls} text-amber-400`} />;
  return <HelpCircle className={`${cls} text-gray-500`} />;
}

function gradeColor(grade) {
  if (grade === 'A') return 'text-emerald-300 bg-emerald-500/15 border-emerald-500/40';
  if (grade === 'B') return 'text-cyan-300 bg-cyan-500/15 border-cyan-500/40';
  if (grade === 'C') return 'text-amber-300 bg-amber-500/15 border-amber-500/40';
  if (grade === 'D') return 'text-orange-300 bg-orange-500/15 border-orange-500/40';
  return 'text-red-300 bg-red-500/15 border-red-500/40';
}

function verdictColor(verdict) {
  if (verdict?.startsWith('NOT A TOKEN')) return 'border-gray-500/40 bg-gray-500/10 text-gray-200';
  if (verdict?.startsWith('AVOID')) return 'border-red-500/40 bg-red-500/10 text-red-200';
  if (verdict?.startsWith('PROBABLY')) return 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200';
  if (verdict?.startsWith('CAUTION')) return 'border-amber-500/40 bg-amber-500/10 text-amber-200';
  return 'border-red-500/40 bg-red-500/10 text-red-200';
}

export default function TokenScannerPage() {
  const [chains, setChains] = useState([]);
  const [address, setAddress] = useState('');
  const [chain, setChain] = useState(DEFAULT_CHAIN);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [ack, setAck] = useState(false);

  const loadHistory = useCallback(async () => {
    try {
      const res = await listRecentTokenScans();
      setHistory((res?.data || res)?.scans || []);
    } catch {}
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await getTokenScannerChains();
        setChains((res?.data || res)?.chains || []);
        loadHistory();
      } catch (e) { setError(e?.message || 'Failed to load'); }
    })();
  }, [loadHistory]);

  async function runScan() {
    if (!address.trim()) { setError('Enter a token contract address'); return; }
    setScanning(true); setError(null); setResult(null); setAck(false);
    try {
      const res = await scanToken({ address: address.trim(), chain });
      setResult(res?.data || res);
      loadHistory();
    } catch (e) {
      setError(e?.message || 'Scan failed');
    } finally { setScanning(false); }
  }

  async function onDelete(scanId) {
    if (!window.confirm('Delete this scan from history?')) return;
    try { await deleteTokenScan(scanId); loadHistory(); } catch (e) { setError(e?.message || 'Delete failed'); }
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-fade-in">
      {/* Hero */}
      <div className="relative overflow-hidden bg-gradient-to-br from-red-500/10 via-dark-800/80 to-dark-900 rounded-2xl border border-red-500/20 p-6">
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-red-400/10 blur-3xl pointer-events-none" />
        <div className="relative flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-red-500/20 border border-red-400/30 flex items-center justify-center flex-shrink-0">
            <ShieldAlert className="w-6 h-6 text-red-400" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold text-white">Token Launch Scanner</h1>
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 border border-red-500/30 font-mono">Elite · Trader's Suite</span>
            </div>
            <p className="text-xs text-red-200/70 mt-1 flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" /> Pre-trade safety screen for new tokens. 9 checks combined into one safety score. Replaces the 5 sites you currently juggle.
              <InfoTip side="bottom">9-of-9 passes does NOT guarantee no rug — novel attack patterns exist. Treat scanner as ONE check (the strong-negative-signal kind), not the only check. CAUTION verdict is highest-judgment zone.</InfoTip>
            </p>
          </div>
        </div>
      </div>

      {/* Input form */}
      <div className="bg-dark-800/60 border border-dark-700 rounded-2xl p-5">
        <div className="grid grid-cols-1 md:grid-cols-[1fr,180px,auto] gap-3 items-end">
          <Field label="Token contract address">
            <input
              value={address} onChange={e => setAddress(e.target.value)}
              placeholder="0x..." className="ws-input font-mono"
            />
          </Field>
          <Field label="Chain">
            <select value={chain} onChange={e => setChain(e.target.value)} className="ws-input">
              {chains.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </Field>
          <button
            onClick={runScan} disabled={scanning || !address.trim()}
            className="px-4 py-2 rounded-lg bg-red-500/20 border border-red-500/40 text-red-200 hover:bg-red-500/30 text-sm font-medium transition flex items-center gap-2 disabled:opacity-50"
          >
            {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            Scan token
          </button>
        </div>
        <div className="text-[11px] text-gray-500 mt-2 flex items-start gap-1.5">
          <Info className="w-3 h-3 flex-shrink-0 mt-0.5" />
          First scan takes ~10-15s (multiple on-chain reads + explorer calls). Subsequent scans of the same token are faster due to caching.
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-sm text-red-300 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {error}
        </div>
      )}

      {scanning && (
        <div className="text-center py-12 text-gray-300">
          <Loader2 className="w-7 h-7 animate-spin mx-auto mb-2 text-red-400" />
          <div className="text-sm">Running 9 safety checks across on-chain + explorer + DexScreener...</div>
        </div>
      )}

      {result && <ScanResult result={result} ack={ack} setAck={setAck} />}

      {/* History */}
      {history.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-white mb-3">Recent scans</h2>
          <div className="bg-dark-800/60 border border-dark-700 rounded-2xl overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-dark-900/40">
                <tr className="text-[10px] uppercase tracking-wider text-gray-500">
                  <th className="text-left py-2.5 px-4 font-medium">When</th>
                  <th className="text-left py-2.5 px-4 font-medium">Token</th>
                  <th className="text-left py-2.5 px-4 font-medium">Chain</th>
                  <th className="text-right py-2.5 px-4 font-medium">Score</th>
                  <th className="text-left py-2.5 px-4 font-medium">Grade</th>
                  <th className="text-right py-2.5 px-4 font-medium">Flags</th>
                  <th className="text-right py-2.5 px-4 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {history.map(h => {
                  // EOA scans store overallScore: null, grade: null — render them
                  // as a clearly different row instead of a misleading "F clean".
                  const notContract = h.overallScore == null;
                  return (
                  <tr key={h.scanId} className="border-t border-dark-700/40">
                    <td className="py-2.5 px-4 text-gray-400 whitespace-nowrap">{new Date(h.asOf).toLocaleString()}</td>
                    <td className="py-2.5 px-4 text-white font-mono text-[11px]">{h.tokenSymbol || '—'} <span className="text-gray-500">{h.address.slice(0, 8)}…</span></td>
                    <td className="py-2.5 px-4 text-gray-300">{h.chainKey}</td>
                    <td className="py-2.5 px-4 text-right text-white font-mono">{notContract ? '—' : h.overallScore}</td>
                    <td className="py-2.5 px-4">
                      {notContract
                        ? <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded border bg-gray-500/15 text-gray-300 border-gray-500/40">EOA</span>
                        : <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded border ${gradeColor(h.grade)}`}>{h.grade}</span>}
                    </td>
                    <td className="py-2.5 px-4 text-right text-[11px]">
                      {notContract && <span className="text-gray-400">not a contract</span>}
                      {!notContract && h.hardFailCount > 0 && <span className="text-red-300">{h.hardFailCount} fail · </span>}
                      {!notContract && h.warningCount > 0 && <span className="text-amber-300">{h.warningCount} warn</span>}
                      {!notContract && h.hardFailCount === 0 && h.warningCount === 0 && <span className="text-emerald-300">clean</span>}
                    </td>
                    <td className="py-2.5 px-4 text-right">
                      <button onClick={() => onDelete(h.scanId)} className="p-1.5 rounded bg-red-500/10 border border-red-500/30 text-red-300 hover:bg-red-500/20">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function ScanResult({ result, ack, setAck }) {
  const hasFail = result.hardFailCount > 0;
  const isNotContract = result.isContract === false;
  return (
    <div className="space-y-4">
      {/* Headline card */}
      <div className="bg-dark-800/60 border border-dark-700 rounded-2xl p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              {isNotContract ? (
                <span className="text-lg font-bold text-gray-400">Not a token contract</span>
              ) : (
                <>
                  <span className="text-lg font-bold text-white font-mono">{result.tokenSymbol || '—'}</span>
                  <span className="text-sm text-gray-400">{result.tokenName || 'Unknown'}</span>
                </>
              )}
              <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border bg-dark-700 text-gray-300 border-dark-600">{result.chainLabel}</span>
            </div>
            <div className="text-[11px] text-gray-500 font-mono mt-1 flex items-center gap-2">
              {result.address}
              {!isNotContract && (
                <a href={`https://app.uniswap.org/swap?inputCurrency=ETH&outputCurrency=${result.address}`} target="_blank" rel="noopener noreferrer" className="text-blue-300 hover:text-blue-200 flex items-center gap-0.5">
                  Uniswap <ExternalLink className="w-2.5 h-2.5" />
                </a>
              )}
            </div>
          </div>
          {/* Score + grade only make sense for actual contracts. For an EOA
              we show a single clear "wallet, not a token" message instead. */}
          {!isNotContract && (
            <div className="text-center">
              <div className={`text-4xl font-bold ${gradeColor(result.grade).split(' ')[0]}`}>{result.overallScore}</div>
              <div className={`text-xs uppercase tracking-wider font-mono px-2 py-0.5 rounded border inline-block mt-1 ${gradeColor(result.grade)}`}>Grade {result.grade}</div>
              <div className="text-[10px] text-gray-500 mt-1">Data completeness: {result.completeness}%</div>
            </div>
          )}
        </div>

        <div className={`mt-4 rounded-xl border p-3 text-sm ${verdictColor(result.verdict)}`}>
          {result.verdict}
        </div>
      </div>

      {/* Per-check breakdown */}
      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-white">Safety checks ({result.checks.length})</h3>
        {result.checks.map(c => (
          <div key={c.id} className="bg-dark-800/60 border border-dark-700 rounded-xl p-4">
            <div className="flex items-start justify-between gap-3 mb-1">
              <div className="flex items-start gap-2 min-w-0 flex-1">
                {statusIcon(c.status, 4)}
                <div className="min-w-0">
                  <div className="text-sm text-white font-semibold">{c.name}</div>
                  <div className="text-[11px] text-gray-500 font-mono">{c.source || '—'} · weight {c.weight}</div>
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-sm font-mono text-white">{c.score} / {c.weight}</div>
                {c.dataPoint && <div className="text-[11px] text-gray-400 font-mono mt-0.5">{c.dataPoint}</div>}
              </div>
            </div>
            <div className="text-xs text-gray-300 mt-1.5 leading-relaxed">{c.explanation}</div>
          </div>
        ))}
      </div>

      {/* Disclosures */}
      <div className="bg-amber-500/5 border border-amber-500/30 rounded-xl p-4">
        <div className="text-xs font-semibold text-amber-300 mb-2 flex items-center gap-1.5">
          <ShieldAlert className="w-3.5 h-3.5" /> Read this before trading
        </div>
        <ul className="text-[11px] text-amber-200/90 space-y-1 list-disc pl-4">
          {result.disclosures.map((d, i) => <li key={i}>{d}</li>)}
        </ul>
      </div>

      {/* Acknowledgment gate */}
      {!hasFail && (
        <div className="bg-dark-800/60 border border-dark-700 rounded-xl p-4">
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={ack} onChange={e => setAck(e.target.checked)} className="mt-0.5" />
            <span className="text-xs text-gray-300">
              I understand this scan is a heuristic, not a guarantee. I will verify the contract source myself before trading.
            </span>
          </label>
          {ack && (
            <div className="mt-3 text-xs text-emerald-300">
              ✓ Acknowledged. Proceed to your preferred DEX with your eyes open.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-gray-500 mb-1 block">{label}</label>
      {children}
    </div>
  );
}
